import { Box, Divider, Menu, MenuItem } from '@mui/material';
import { List, LogOut } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import BreadcrumbButton from '~/components/Buttons/BreadcrumbButton';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import PatientProperties from '~/components/Properties/PatientProperties';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Patient } from '~/domain/patient/patient';
import useSensitiveInfoReveal from '~/hooks/patient/useSensitiveInfoReveal';
import useUnloadPatient from '~/hooks/patient/useUnloadPatient';
import {
    pendingEntryCount,
    usePendingChanges,
} from '~/hooks/save/usePendingChanges';
import { useSavePatientChanges } from '~/hooks/save/useSavePatientChanges';
import useBreadcrumbMenu from '~/hooks/useBreadcrumbMenu';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { calculateAge, formatDateCompact } from '~/utils/date';
import PatientInfoHiddenChip from '../PatientInfoHiddenChip';
import ReturnToListButton from '../ReturnToListButton';
import { useReturnToPatientList } from '../useReturnToPatientList';
import SensitiveInfoToggle from './SensitiveInfoToggle';

interface PatientMenuProps {
    patient: Patient;
}

export default function PatientMenu({ patient }: PatientMenuProps) {
    const { buttonProps, menuProps, close } = useBreadcrumbMenu();
    const intl = useIntl();
    const {
        isInfoShown: showSensitiveInfo,
        isConfirmOpen: revealConfirmOpen,
        requestReveal,
        confirmReveal,
        cancelReveal,
        hideInfo,
    } = useSensitiveInfoReveal();
    const hasUnsavedChanges = useRecoilValue(hasUnsavedChangesState);
    const { unloadCurrentPatient } = useUnloadPatient();
    const { available: canReturnToList, returnToList } =
        useReturnToPatientList();
    const { state: pendingChanges, canSave } = usePendingChanges();
    const save = useSavePatientChanges();
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
    const [confirmReturnOpen, setConfirmReturnOpen] = useState(false);

    // "Save and close" is offered when there are pending dataset changes
    // this session can actually persist. Out-of-scope changes (dose display
    // settings, plans) cannot be saved and are lost either way — the base
    // warning covers them.
    const canSaveAndClose = canSave && pendingEntryCount(pendingChanges) > 0;

    const patientName = patient.dataSet.PatientName || 'Patient';
    const patientId = patient.dataSet.PatientID || 'N/A';
    const patientSexCode = patient.dataSet.PatientSex;
    const patientDob = patient.dataSet.PatientBirthDate;
    const formattedDob = patientDob ? formatDateCompact(patientDob) : undefined;
    const age = patientDob ? calculateAge(patientDob) : undefined;
    const dobWithAge =
        formattedDob && age !== undefined
            ? `${formattedDob} (${age}y)`
            : formattedDob;

    const sexMessageIds: Record<string, string> = {
        M: 'sex.male',
        F: 'sex.female',
        O: 'sex.other',
    };
    const patientSex = patientSexCode
        ? intl.formatMessage({
              id: sexMessageIds[patientSexCode.toUpperCase()] || 'sex.unknown',
          })
        : undefined;

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.key.toLowerCase() === 'h') {
                // preventDefault stops the input's Ctrl+H = backspace behavior
                // (and the browser's history-panel shortcut). stopPropagation
                // would be redundant because capture-phase listeners run
                // before the target's own handlers.
                event.preventDefault();
                if (showSensitiveInfo) {
                    hideInfo();
                    return;
                }
                if (revealConfirmOpen) {
                    return;
                }
                requestReveal();
            }
        };
        // Capture phase so the toggle fires even when focus is in a text
        // input that calls stopPropagation in its own onKeyDown.
        document.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => {
            document.removeEventListener('keydown', handleKeyDown, {
                capture: true,
            });
        };
    }, [showSensitiveInfo, revealConfirmOpen, hideInfo, requestReveal]);

    const handleClosePatient = useCallback(() => {
        close();
        setConfirmCloseOpen(true);
    }, [close]);

    const handleConfirmClose = useCallback(() => {
        setConfirmCloseOpen(false);
        unloadCurrentPatient();
    }, [unloadCurrentPatient]);

    // Returning also closes the patient, so it is confirmed on the same terms.
    const handleReturnToList = useCallback(() => {
        close();
        setConfirmReturnOpen(true);
    }, [close]);

    const handleConfirmReturn = useCallback(() => {
        setConfirmReturnOpen(false);
        returnToList();
    }, [returnToList]);

    /**
     * Saves everything, then closes only on full success. On a partial
     * failure or conflict the patient stays open — the save state is
     * shared across hook instances (saveAtoms), so conflicts from THIS
     * save open the standard conflict dialog rendered by the header
     * indicator; closing would discard the very changes the user just
     * asked to keep.
     */
    const handleSaveAndClose = useCallback(
        async (close: () => void) => {
            const result = await save.saveAll();
            if (result?.allSaved) {
                setConfirmCloseOpen(false);
                setConfirmReturnOpen(false);
                // flushSync so the unload and its navigation commit
                // together, exactly like the synchronous confirm path.
                //
                // This once guarded something sharper: useWorkflowSteps
                // redirected to /setup the moment a step's requirements
                // went away, and that redirect clobbered returnToList's
                // navigate to the list, stranding the user in an empty
                // viewer. Steps are search parameters now and nothing
                // redirects, so what is left is only keeping the unload
                // and the navigation in one commit — likely removable,
                // but not on the word of this comment alone.
                flushSync(() => {
                    close();
                });
            }
        },
        [save]
    );

    const saveAndCloseAction = canSaveAndClose
        ? {
              label: intl.formatMessage({ id: 'saveChanges.saveAndClose' }),
              testId: 'save-and-close-button',
          }
        : undefined;

    return (
        <>
            {/* Sits before the patient card, as it does in the concept
                prototype. It lives here rather than beside the card because
                returning closes the patient, and the confirmation for that —
                including its unsaved-changes wording and its Save and close —
                is already owned here. A second copy of that dialog is how the
                two would come to disagree about what closing costs. */}
            {canReturnToList && (
                <ReturnToListButton onClick={handleReturnToList} />
            )}
            <BreadcrumbButton
                {...buttonProps}
                data-testid="patient-breadcrumb"
                flipped
                topContent={
                    showSensitiveInfo ? (
                        <SensitiveInfoTypography>
                            {patientName}
                        </SensitiveInfoTypography>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <PatientInfoHiddenChip />
                        </Box>
                    )
                }
                label={
                    showSensitiveInfo ? (
                        <SensitiveInfoTypography
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            {[patientSex, patientId, dobWithAge]
                                .filter(Boolean)
                                .flatMap((item, index, arr) =>
                                    index < arr.length - 1
                                        ? [
                                              item,
                                              <Divider
                                                  key={`divider-${index}`}
                                                  orientation="vertical"
                                                  flexItem
                                                  sx={{
                                                      mx: 0.5,
                                                      height: 12,
                                                  }}
                                              />,
                                          ]
                                        : [item]
                                )}
                        </SensitiveInfoTypography>
                    ) : null
                }
            />

            <Menu {...menuProps}>
                <Box position="relative" px={1} pt={1} pr={5}>
                    <Box
                        sx={
                            !showSensitiveInfo
                                ? {
                                      filter: 'blur(0.5rem)',
                                      userSelect: 'none',
                                  }
                                : undefined
                        }
                    >
                        <PatientProperties
                            dataSet={patient.dataSet}
                            showTitle={false}
                        />
                    </Box>
                    <Box position="absolute" top={0} right={0}>
                        <SensitiveInfoToggle />
                    </Box>
                </Box>

                <Divider flexItem sx={{ my: 0.5 }} />

                {canReturnToList && (
                    <MenuItem
                        onClick={handleReturnToList}
                        sx={{ fontWeight: 500 }}
                        data-testid="patient-list-menu-item"
                    >
                        <FormattedMessage id="backToPatientList" />
                        <List
                            style={{ marginLeft: 'auto', fontSize: 12 }}
                            size={12}
                        />
                    </MenuItem>
                )}

                <MenuItem
                    onClick={handleClosePatient}
                    sx={{ fontWeight: 500 }}
                    data-testid="close-patient-menu-item"
                >
                    <FormattedMessage id="closePatient" />
                    <LogOut
                        style={{ marginLeft: 'auto', fontSize: 12 }}
                        size={12}
                    />
                </MenuItem>
            </Menu>

            <ConfirmationDialog
                open={confirmReturnOpen}
                onCancel={() => setConfirmReturnOpen(false)}
                onConfirm={handleConfirmReturn}
                busy={save.saving}
                extraAction={
                    saveAndCloseAction && {
                        ...saveAndCloseAction,
                        onClick: () => void handleSaveAndClose(returnToList),
                    }
                }
                title={intl.formatMessage({ id: 'backToPatientList' })}
                message={intl.formatMessage({
                    id: hasUnsavedChanges
                        ? 'backToPatientListConfirmationUnsaved'
                        : 'backToPatientListConfirmation',
                })}
                confirmText={intl.formatMessage({
                    id: hasUnsavedChanges ? 'closeWithoutSaving' : 'close',
                })}
            />

            <ConfirmationDialog
                open={confirmCloseOpen}
                onCancel={() => setConfirmCloseOpen(false)}
                onConfirm={handleConfirmClose}
                busy={save.saving}
                extraAction={
                    saveAndCloseAction && {
                        ...saveAndCloseAction,
                        onClick: () =>
                            void handleSaveAndClose(unloadCurrentPatient),
                    }
                }
                title={intl.formatMessage({ id: 'closePatient' })}
                message={intl.formatMessage({
                    id: hasUnsavedChanges
                        ? 'closePatientConfirmationUnsaved'
                        : 'closePatientConfirmation',
                })}
                confirmText={intl.formatMessage({
                    id: hasUnsavedChanges ? 'closeWithoutSaving' : 'close',
                })}
            />

            <ConfirmationDialog
                open={revealConfirmOpen}
                onCancel={cancelReveal}
                onConfirm={confirmReveal}
                mode="warning"
                title={intl.formatMessage({
                    id: 'revealPatientInfoConfirmationTitle',
                })}
                message={intl.formatMessage({
                    id: 'revealPatientInfoConfirmation',
                })}
            />
        </>
    );
}
